import React from "react";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import type { Theme } from "@mui/material/styles";
import type { EstadiasPrevias } from "../hooks/useEstadiasPrevias";

/** Con más de estos, la línea se vuelve ilegible: el resto se resume en "+N". */
const MAX_DEPTOS_VISIBLES = 3;

const ordinal = (n: number) => (n === 1 ? "1ª" : `${n}ª`);

export const EstadiasPreviasChip: React.FC<{
  datos: EstadiasPrevias;
  onVerHistorial?: () => void;
  /** En listados densos entran menos departamentos que en el detalle. */
  maxDeptos?: number;
}> = ({ datos, onVerHistorial, maxDeptos = MAX_DEPTOS_VISIBLES }) => {
  if (datos.cargando) {
    return <Skeleton variant="text" width={150} sx={{ fontSize: "0.75rem" }} />;
  }

  if (datos.cantidad === 0) {
    // En un listado no se marca cada fila nueva: sólo se destaca lo excepcional.
    if (maxDeptos < MAX_DEPTOS_VISIBLES) return null;
    return (
      <Typography variant="caption" color="text.secondary">
        Primera estadía
      </Typography>
    );
  }

  const visibles = datos.departamentos.slice(0, Math.max(1, maxDeptos));
  const restantes = datos.departamentos.length - visibles.length;

  // El acento va sólo en el conteo, que es lo que hace levantar la vista. Los
  // departamentos van en tono secundario: informan, no compiten con el nombre
  // del cliente, que es el dato principal de la ficha.
  const contenido = (
    <>
      <HistoryIcon sx={{ fontSize: 14, flexShrink: 0, color: "text.disabled" }} />
      <Box component="span" className="estadias-conteo" sx={{ fontWeight: 500, color: "primary.main" }}>
        {ordinal(datos.numeroDeEstadia)} estadía
      </Box>
      <Box component="span">
        · antes en {visibles.join(" · ")}
        {restantes > 0 ? ` +${restantes}` : ""}
      </Box>
    </>
  );

  const sx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    flexWrap: "wrap" as const,
    fontSize: "0.75rem",
    lineHeight: 1.5,
    color: "text.secondary",
  };

  if (!onVerHistorial) {
    return (
      <Typography component="span" sx={sx}>
        {contenido}
      </Typography>
    );
  }

  return (
    <Tooltip title="Ver historial completo del cliente">
      <Typography
        component="button"
        type="button"
        onClick={onVerHistorial}
        sx={{
          ...sx,
          border: 0,
          p: 0,
          bgcolor: "transparent",
          font: "inherit",
          fontSize: "0.75rem",
          textAlign: "left",
          cursor: "pointer",
          // El subrayado va sólo bajo el conteo, nunca bajo la línea entera: la
          // regla corrida hacía leer todo el texto como un enlace. Se mantiene
          // en reposo, punteado y tenue, porque es la única señal de que esto
          // se puede tocar, y en una pantalla táctil no hay hover que la
          // revele. Al apuntar pasa a sólido.
          "& .estadias-conteo": {
            textDecorationLine: "underline",
            textDecorationStyle: "dotted",
            textDecorationColor: (t: Theme) => alpha(t.palette.primary.main, 0.45),
            textUnderlineOffset: 3,
          },
          // Sólo donde hay puntero real: en táctil el :hover queda pegado
          // después del toque y el estado no se limpia hasta tocar otra cosa.
          "@media (hover: hover)": {
            "&:hover .estadias-conteo": {
              textDecorationStyle: "solid",
              textDecorationColor: "currentColor",
            },
          },
          "&:focus-visible .estadias-conteo": {
            textDecorationStyle: "solid",
            textDecorationColor: "currentColor",
          },
        }}
      >
        {contenido}
      </Typography>
    </Tooltip>
  );
};

export default EstadiasPreviasChip;
